import { formatCurrency } from '@crm/shared';
import { Button, Spinner } from '@crm/ui';
import { CheckCircle2, CreditCard } from 'lucide-react';
import { useState } from 'react';
import { StatusHeader, SummaryRow } from '@/components';
import { toErrorMessage } from '@/modules/auth/utils';
import {
  type BillingOrder,
  MessagePacks,
  useCheckout,
  useMessagePacks,
  useOpenOrder,
  useSyncOrder,
} from '@/modules/billing';
import type { PhoneLine } from '../api';

interface ChargeStepProps {
  linha: PhoneLine;
  onPaid: () => void;
}

const AVISO =
  'O preco das mensagens acompanha a tabela da Meta e pode mudar de um mes para o outro.';

export function ChargeStep({ linha, onPaid }: ChargeStepProps) {
  const aberta = useOpenOrder(linha.id);
  const packs = useMessagePacks();
  const gerar = useCheckout();
  const conferir = useSyncOrder();
  const [escolhido, setEscolhido] = useState(0);

  const order = aberta.data?.order ?? null;
  const mensalidade = order?.lineAmount ?? linha.openCharge?.amount ?? 0;
  const opcoes = packs.data?.data ?? [];
  const pack = opcoes.find((item) => item.messages === escolhido) ?? null;

  const processando = gerar.isPending || conferir.isPending;
  const erro = gerar.error ?? conferir.error;
  const total = order ? order.total : mensalidade + (pack?.price ?? 0);

  // Quem ja pagou nao pode ser mandado de volta para a pagina de pagamento:
  // por isso a cobranca existente e reconferida antes de sair daqui.
  function seguir(atual: BillingOrder) {
    if (atual.status === 'PAID') {
      onPaid();
      return;
    }

    if (atual.checkoutUrl) window.location.assign(atual.checkoutUrl);
  }

  function pagar() {
    if (order) {
      conferir.mutate(order.id, { onSuccess: seguir });
      return;
    }

    gerar.mutate({ lineId: linha.id, messages: escolhido }, { onSuccess: seguir });
  }

  if (aberta.isLoading || packs.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  if (total === 0) {
    return (
      <StatusHeader
        icon={<CheckCircle2 className="h-5 w-5" />}
        tone="ok"
        title="Nao ha nada a cobrar"
        detail="A mensalidade da linha esta em dia."
      />
    );
  }

  return (
    <div className="space-y-6">
      <StatusHeader
        icon={<CreditCard className="h-5 w-5" />}
        title="Mensalidade"
        detail="A linha e as mensagens do mes em uma unica cobranca."
      />

      {order ? (
        <div className="space-y-2 border-t border-border pt-4">
          <SummaryRow label="Linha de WhatsApp" value={formatCurrency(order.lineAmount)} />
          {order.messages > 0 ? (
            <SummaryRow
              label={`${order.messages.toLocaleString('pt-BR')} mensagens`}
              value={formatCurrency(order.creditAmount)}
            />
          ) : null}
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-t border-border pt-2">
            <span className="font-medium">Total</span>
            <span className="text-lg font-semibold">{formatCurrency(order.total)}</span>
          </div>
        </div>
      ) : (
        <div className="border-t border-border pt-4">
          <MessagePacks
            packs={opcoes}
            escolhido={escolhido}
            onPick={setEscolhido}
            base={mensalidade}
            baseLabel="Linha"
            emptyLabel="Somente a linha"
          />
        </div>
      )}

      <p className="text-xs text-muted-foreground">{AVISO}</p>

      <div className="flex justify-center">
        <Button
          type="button"
          size="lg"
          className="h-14 w-full text-base sm:h-14 sm:w-auto sm:min-w-64"
          disabled={processando}
          onClick={pagar}
        >
          {processando ? <Spinner /> : <CreditCard className="h-4 w-4" />}
          Pagar
        </Button>
      </div>

      {erro ? <p className="text-center text-sm text-destructive">{toErrorMessage(erro)}</p> : null}
    </div>
  );
}
